import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { businessInfo } from '../mock/data';

const PrivacyPolicy = () => {
  const lastUpdated = "May 26, 2026";

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-[#152238] text-white py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link 
            to="/" 
            className="inline-flex items-center space-x-2 text-[#A8B5C8] hover:text-white transition-colors mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to Home</span>
          </Link>
          <h1 className="text-3xl md:text-4xl font-bold">Privacy Policy</h1>
          <p className="text-[#A8B5C8] mt-2">Last updated: {lastUpdated}</p>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="prose max-w-none space-y-8 text-gray-800">

          <section>
            <p className="text-lg leading-relaxed">
              {businessInfo.name} LLC ("we," "us," or "our") respects your privacy and is committed to protecting the personal information you share with us. This Privacy Policy explains what information we collect, how we use it, and the limited circumstances in which we share it.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[#152238] mb-4">1. Information We Collect</h2>
            <p className="mb-3">We may collect the following types of personal information from you:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Contact information:</strong> name, mailing address, email address, and phone number.</li>
              <li><strong>Household and demographic information:</strong> date of birth, dependents, household size, estimated household income, and tobacco use (as required for ACA plan quoting and enrollment).</li>
              <li><strong>Identification information:</strong> Social Security Number, immigration or citizenship status (where required by the ACA marketplace for enrollment).</li>
              <li><strong>Health-related information:</strong> general health status, prescriptions, preferred doctors, and coverage preferences voluntarily shared to help identify suitable plans.</li>
              <li><strong>Communication preferences and consent records,</strong> including SMS/text message opt-in records.</li>
              <li><strong>Technical information,</strong> such as IP address and browser type when you visit our website.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[#152238] mb-4">2. How We Use Your Information</h2>
            <p className="mb-3">We use your personal information to:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Provide quotes and enroll you in health insurance plans (ACA marketplace and private off-market).</li>
              <li>Communicate with you about your policy, enrollment status, appointments, and service requests.</li>
              <li>Service your account, including payment processing, ID card orders, PCP selection, and carrier communications on your behalf.</li>
              <li>Comply with legal, regulatory, and licensing obligations.</li>
              <li>Improve our website, services, and customer experience.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[#152238] mb-4">3. Who We Share Information With</h2>
            <p className="mb-3">We only share your personal information with parties necessary to deliver the services you request:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Insurance carriers and marketplaces</strong> (such as Healthcare.gov and state-based exchanges) to submit applications and enroll you in coverage.</li>
              <li><strong>Service providers</strong> that support our operations (such as CRM, quoting, and secure communication platforms), bound by confidentiality obligations.</li>
              <li><strong>Government or regulatory agencies</strong> when required by law or legal process.</li>
            </ul>
            <div className="bg-[#FDF6E3] border border-[#F0E5C8] rounded-lg p-5 mt-5">
              <p className="font-semibold text-[#152238] mb-2">SMS / Text Message Consent</p>
              <p>
                <strong>SMS consent and mobile opt-in information are not shared with third parties or affiliates for marketing purposes.</strong> Your mobile number and SMS opt-in data are used solely to communicate with you about your inquiries, policies, and service requests.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[#152238] mb-4">4. SMS / Text Messaging</h2>
            <p className="mb-3">
              By providing your mobile number and opting in, you consent to receive text messages from {businessInfo.name} LLC related to quotes, enrollment, policy updates, appointment reminders, and customer service.
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Messaging frequency may vary.</li>
              <li>Message and data rates may apply.</li>
              <li>Reply <strong>STOP</strong> to any message to opt out at any time.</li>
              <li>Reply <strong>HELP</strong> for assistance, or contact us at {businessInfo.phone}.</li>
            </ul>
            <p className="mt-3">
              See our <Link to="/terms-conditions" className="text-[#3B5275] underline hover:text-[#152238]">Terms & Conditions</Link> for full SMS program details.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[#152238] mb-4">5. How We Protect Your Information</h2>
            <p>
              We use reasonable administrative, technical, and physical safeguards to protect your personal information from unauthorized access, use, or disclosure. However, no method of transmission over the Internet is 100% secure.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[#152238] mb-4">6. Your Rights and Choices</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>You may request access to or correction of the personal information we hold about you.</li>
              <li>You may withdraw consent for marketing communications at any time.</li>
              <li>You may opt out of SMS by replying STOP to any text message.</li>
              <li>Residents of certain states (including California) may have additional rights under state privacy laws.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[#152238] mb-4">7. Children's Privacy</h2>
            <p>
              Our services are not directed to individuals under 18, and we do not knowingly collect personal information from children (except as part of a household application submitted by a parent or legal guardian).
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[#152238] mb-4">8. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. Changes will be posted on this page with an updated "Last updated" date.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-[#152238] mb-4">9. Contact Us</h2>
            <p className="mb-2">For privacy questions or requests, please contact:</p>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-5">
              <p><strong>{businessInfo.name} LLC</strong></p>
              <p>Attn: {businessInfo.agentName}, {businessInfo.agentTitle}</p>
              <p>{businessInfo.location}</p>
              <p>Phone: <a href={`tel:${businessInfo.phone}`} className="text-[#3B5275] underline">{businessInfo.phone}</a></p>
              <p>Email: <a href={`mailto:${businessInfo.email}`} className="text-[#3B5275] underline break-all">{businessInfo.email}</a></p>
              <p>NPN: {businessInfo.npn}</p>
            </div>
          </section>

        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
